/**
 * Inbound: open purchase orders, reorder suggestions and the supplier scorecard.
 *
 * The three sit on one screen because "should I order" is one question. The old app spread
 * it across three tabs and produced two different answers for cash in flight, which is why
 * the KPI strip here names them separately: PO commitment is what is still owed, inbound
 * value is what will be owned. They are not merged and never will be.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Card, EmptyState, KpiTile, MoneyText, Pill, Skeleton, useToast } from '@/components';
import { useSession } from '@/app/session';
import { ApiProblem } from '@/lib/api';
import { formatCount, formatPercent, formatVelocity } from '@/lib/format';
import type { MetricEnvelope } from '@elutide/shared';
import { NewPoDrawer } from './NewPoDrawer';
import { PO_PIPELINE_LABELS, poHasMoney } from './types';
import type { NewPoLineDraft, SuggestionRow } from './types';
import { useAdvancePo, usePurchaseOrders, useSnoozeSuggestion, useSuggestions, useSuppliers } from './queries';
import './stock.css';

function point(metricId: string, value: number | null, unit: MetricEnvelope['unit']): MetricEnvelope {
  return {
    metric_id: metricId,
    version: 1,
    value,
    unit,
    basis: 'ledger',
    window: { kind: 'point', start: null, end: null, tz: 'America/New_York', label: 'now' },
    as_of: new Date().toISOString(),
    sources: ['purchase_orders', 'po_lines'],
    dims: null,
    partial: false,
    coverage: null,
  };
}

/** The five step bar. `paid` and `customs` are side steps and do not move it. */
function Pipeline({ step }: { step: number }) {
  return (
    <span className="stock-pipeline" aria-label={`Step ${step} of ${PO_PIPELINE_LABELS.length}`}>
      {PO_PIPELINE_LABELS.map((entry, index) => (
        <span
          key={entry.status}
          className={`stock-pipeline__step${index < step ? ' stock-pipeline__step--done' : ''}`}
          title={entry.label}
        />
      ))}
    </span>
  );
}

const NEXT_STATUS: Record<string, string> = {
  draft: 'ordered',
  ordered: 'in_transit',
  paid: 'in_transit',
  in_transit: 'received',
  customs: 'received',
  received: 'qc',
  qc: 'available',
  available: 'closed',
};

export function InboundPage() {
  const { can } = useSession();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState<string | null>(
    'ordered,paid,in_transit,customs,received,qc',
  );
  const [poOpen, setPoOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ supplier_id: string | null; lines: NewPoLineDraft[] } | null>(null);

  const pos = usePurchaseOrders(statusFilter, null);
  const suggestions = useSuggestions();
  const suppliers = useSuppliers(true);
  const advance = useAdvancePo();
  const snooze = useSnoozeSuggestion();

  // Raising a PO is po.manage; snoozing a suggestion is stock.set_reorder (the API says the
  // same, POST /po/suggestions/:id/snooze). A packer holds neither and sees neither button.
  const canManagePo = can('po.manage');
  const canSetReorder = can('stock.set_reorder');

  const openPos = pos.data?.items ?? [];
  // A packer's rows carry no landed cost and her suggestions no last cost (CONTRACTS.md
  // 3.6): the columns are not rendered rather than rendered empty.
  const showPoMoney = openPos.some(poHasMoney);
  const showSuggestionMoney = (suggestions.data?.items ?? []).some(
    (s) => s.last_unit_cost_cents !== undefined,
  );
  const pastDue = openPos.filter(
    (po) => po.expected_at !== null && po.expected_at < new Date().toISOString().slice(0, 10),
  ).length;
  const needsPo = (suggestions.data?.items ?? []).filter((s) => !s.covered).length;

  const startPo = (suggestion: SuggestionRow) => {
    setPrefill({
      supplier_id: suggestion.preferred_supplier_id,
      lines: [
        {
          product_id: suggestion.product_id,
          sku: suggestion.sku,
          name: suggestion.name,
          qty_ordered: suggestion.suggested_qty,
          unit_cost_cents: suggestion.last_unit_cost_cents ?? 0,
        },
      ],
    });
    setPoOpen(true);
  };

  const advanceTo = async (poId: string, from: string) => {
    const to = NEXT_STATUS[from];
    if (!to) return;
    try {
      await advance.mutateAsync({ poId, toStatus: to, reason: null });
      toast.push({ tone: 'ok', message: `Purchase order moved to ${to.replace(/_/g, ' ')}.` });
    } catch (error) {
      toast.push({
        tone: 'crit',
        message: error instanceof Error ? error.message : 'The purchase order did not move.',
        requestId: error instanceof ApiProblem ? error.shortRequestId : null,
      });
    }
  };

  return (
    <div className="stock-page">
      <div className="o-kpis stock-kpis">
        {/* The two valuation tiles are absent from a packer's response (CONTRACTS.md 3.6),
            and so are the tiles: the strip starts at Past due for her. Loading shows them
            for everyone, because "no data" is the honest state until the response lands. */}
        {pos.data && !pos.data.kpis ? null : (
          <>
            <KpiTile
              label="PO commitment"
              envelope={point('po_balance_due', pos.data?.kpis?.po_balance_due ?? null, 'cents')}
              note={`${openPos.length} open purchase orders, cash still owed`}
            />
            <KpiTile
              label="Inbound at landed cost"
              envelope={point('pending_inbound_value', pos.data?.kpis?.pending_inbound_value ?? null, 'cents')}
              note="What will be owned. Never merged with the commitment."
            />
          </>
        )}
        <KpiTile
          label="Past due"
          envelope={point('po_past_due', pastDue, 'count')}
          tone={pastDue > 0 ? 'warn' : 'ok'}
        />
        <KpiTile
          label="Needs a PO"
          envelope={point('reorder_needed', needsPo, 'count')}
          tone={needsPo > 0 ? 'crit' : 'ok'}
          note="days left at or below lead p80 plus safety"
        />
      </div>

      <Card
        title="Open purchase orders"
        basis="ledger"
        action={
          can('po.manage') ? (
            <button
              type="button"
              className="o-btn o-btn--sm"
              onClick={() => {
                setPrefill(null);
                setPoOpen(true);
              }}
            >
              New PO
            </button>
          ) : null
        }
      >
        <div className="stock-filters" data-allow-hscroll>
          <button
            type="button"
            className="o-chip"
            aria-pressed={statusFilter !== null}
            onClick={() => setStatusFilter('ordered,paid,in_transit,customs,received,qc')}
          >
            Active
          </button>
          <button
            type="button"
            className="o-chip"
            aria-pressed={statusFilter === null}
            onClick={() => setStatusFilter(null)}
          >
            All
          </button>
        </div>

        {pos.isLoading ? (
          <Skeleton shape="row" lines={4} />
        ) : openPos.length === 0 ? (
          <EmptyState message="No purchase orders match this filter." />
        ) : (
          <div className="stock-scroll" data-allow-hscroll>
            <table className="stock-table">
              <caption className="o-visually-hidden">Purchase orders</caption>
              <thead>
                <tr>
                  <th scope="col">PO</th>
                  <th scope="col">Supplier</th>
                  <th scope="col" className="stock-num">Lines</th>
                  <th scope="col" className="stock-num">Units</th>
                  {showPoMoney ? <th scope="col" className="stock-num">Total landed</th> : null}
                  <th scope="col">Status</th>
                  <th scope="col">Ordered</th>
                  <th scope="col">Expected</th>
                  <th scope="col">Lead vs p80</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {openPos.map((po) => (
                  <tr key={po.po_id}>
                    <td>
                      <Link
                        to="/stock/inbound/$poId"
                        params={{ poId: po.po_id }}
                        className="o-mono o-link"
                      >
                        {po.po_number}
                      </Link>
                    </td>
                    <td>{po.supplier_name}</td>
                    <td className="stock-num">{po.line_count}</td>
                    <td className="stock-num">{formatCount(po.unit_count)}</td>
                    {poHasMoney(po) ? (
                      <td className="stock-num">
                        <MoneyText cents={po.total_landed_cents} />
                      </td>
                    ) : null}
                    <td>
                      <Pill tone={po.status === 'available' ? 'ok' : 'warn'}>
                        {po.status.replace(/_/g, ' ')}
                      </Pill>{' '}
                      <Pipeline step={po.pipeline_step} />
                    </td>
                    <td>{po.ordered_at ?? 'not sent'}</td>
                    <td>{po.expected_at ?? 'unknown'}</td>
                    <td className="o-mono">
                      {po.supplier_lead_p80 === null
                        ? 'no measurement'
                        : `p80 ${Math.round(po.supplier_lead_p80)}d`}
                    </td>
                    <td>
                      {can('po.manage') && NEXT_STATUS[po.status] ? (
                        <button
                          type="button"
                          className="o-btn o-btn--ghost o-btn--sm"
                          disabled={advance.isPending}
                          onClick={() => void advanceTo(po.po_id, po.status)}
                        >
                          Advance
                        </button>
                      ) : null}{' '}
                      <Link
                        to="/stock/inbound/$poId"
                        params={{ poId: po.po_id }}
                        className="o-btn o-btn--ghost o-btn--sm"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="stock-ledger__grid">
        <Card
          title="Reorder suggestions"
          basis="woo_paid"
          action={<span className="o-muted">30d velocity, lead p80 plus safety, then cover</span>}
        >
          {suggestions.isLoading ? (
            <Skeleton shape="row" lines={4} />
          ) : (suggestions.data?.items.length ?? 0) === 0 ? (
            <EmptyState message="Nothing needs ordering. Every visible SKU is covered." />
          ) : (
            <div className="stock-scroll" data-allow-hscroll>
              <table className="stock-table">
                <caption className="o-visually-hidden">Reorder suggestions</caption>
                <thead>
                  <tr>
                    <th scope="col">SKU</th>
                    <th scope="col" className="stock-num">On hand</th>
                    <th scope="col" className="stock-num">Velocity</th>
                    <th scope="col">Runs out</th>
                    <th scope="col">Inbound</th>
                    <th scope="col">Lead p80</th>
                    <th scope="col" className="stock-num">Suggest</th>
                    {showSuggestionMoney ? <th scope="col">Last cost</th> : null}
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody>
                  {(suggestions.data?.items ?? []).map((suggestion) => (
                    <tr key={suggestion.product_id} className={suggestion.covered ? 'stock-row--muted' : ''}>
                      <td>
                        <span className="o-mono">{suggestion.sku ?? suggestion.name}</span>
                        {suggestion.urgency === 'critical' ? (
                          <>
                            {' '}
                            <Pill tone="crit">cannot be avoided</Pill>
                          </>
                        ) : null}
                      </td>
                      <td className="stock-num">{formatCount(suggestion.available_qty)}</td>
                      <td className="stock-num">{formatVelocity(suggestion.velocity_30d)}</td>
                      <td>{suggestion.stockout_date ?? 'no sales'}</td>
                      <td>
                        {suggestion.inbound_qty > 0 ? (
                          <Pill tone="info">{`+${suggestion.inbound_qty}`}</Pill>
                        ) : (
                          <span className="o-muted">none</span>
                        )}
                      </td>
                      <td className="o-mono">
                        {Math.round(suggestion.lead_p80_days)}d
                        <span className="o-muted"> n={suggestion.lead_sample_n}</span>
                      </td>
                      <td className="stock-num">
                        {suggestion.covered ? (
                          <span className="o-muted">covered</span>
                        ) : (
                          formatCount(suggestion.suggested_qty)
                        )}
                      </td>
                      {suggestion.last_unit_cost_cents === undefined ? null : (
                        <td>
                          {suggestion.last_unit_cost_cents === null ? (
                            <span className="o-muted">n/a</span>
                          ) : (
                            <>
                              {suggestion.preferred_supplier_name ?? 'unknown'}{' '}
                              <MoneyText cents={suggestion.last_unit_cost_cents} showCents />
                            </>
                          )}
                        </td>
                      )}
                      <td>
                        {suggestion.covered ? null : (
                          <>
                            {canManagePo ? (
                              <button
                                type="button"
                                className="o-btn o-btn--sm"
                                onClick={() => startPo(suggestion)}
                              >
                                PO
                              </button>
                            ) : null}{' '}
                            {canSetReorder ? (
                              <button
                                type="button"
                                className="o-btn o-btn--ghost o-btn--sm"
                                onClick={() =>
                                  snooze.mutate({ productId: suggestion.product_id, days: 7, note: null })
                                }
                              >
                                Snooze 7d
                              </button>
                            ) : null}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Supplier scorecard" basis="ledger">
          {suppliers.isLoading ? (
            <Skeleton shape="row" lines={4} />
          ) : (
            <table className="stock-table">
              <caption className="o-visually-hidden">Supplier performance</caption>
              <thead>
                <tr>
                  <th scope="col">Supplier</th>
                  <th scope="col" className="stock-num">Open POs</th>
                  <th scope="col">Lead p50 / p80</th>
                  <th scope="col" className="stock-num">On time</th>
                  <th scope="col">Terms</th>
                </tr>
              </thead>
              <tbody>
                {(suppliers.data?.items ?? []).map((supplier) => (
                  <tr key={supplier.supplier_id}>
                    <td>{supplier.name}</td>
                    <td className="stock-num">{supplier.open_po_count}</td>
                    <td className="o-mono">
                      {supplier.lead_insufficient_data ? (
                        <span className="o-muted">
                          insufficient data, using {supplier.default_lead_days}d
                        </span>
                      ) : (
                        <>
                          {Math.round(supplier.lead_days_median ?? 0)}d /{' '}
                          {Math.round(supplier.lead_days_p80 ?? 0)}d
                          <span className="o-muted"> n={supplier.lead_sample_n}</span>
                        </>
                      )}
                    </td>
                    <td className="stock-num">
                      {supplier.on_time_pct === null ? (
                        <span className="o-muted">n/a</span>
                      ) : (
                        formatPercent(supplier.on_time_pct)
                      )}
                    </td>
                    <td>{supplier.payment_terms ?? 'not recorded'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <NewPoDrawer open={poOpen} onClose={() => setPoOpen(false)} prefill={prefill} />
    </div>
  );
}
